// ── Zangle DAW project document ─────────────────────────────────────────────
// The Project object is the single source of truth: plain, serializable,
// JSON-friendly. The audio engine rebuilds/partially patches its graph from
// it. Timing model: 1 step = one 16th note; a bar = 16 steps (4/4).

export type OscWave = 'sine' | 'square' | 'sawtooth' | 'triangle';
export type FilterType = 'lowpass' | 'highpass' | 'bandpass';
export type LfoTarget = 'filter' | 'pitch';

export interface SynthParams {
  osc1Wave: OscWave;
  osc1Octave: number; // -2..2
  osc1Detune: number; // cents, -100..100
  osc1Level: number; // 0..1
  osc2Wave: OscWave;
  osc2Octave: number;
  osc2Detune: number;
  osc2Level: number;
  fmDepth: number; // 0..1 (osc2 → osc1)
  filterType: FilterType;
  filterCutoff: number; // Hz, 40..18000
  filterResonance: number; // Q, 0..20
  filterEnvAmount: number; // 0..1
  lfoRate: number; // Hz, 0.05..25
  lfoDepth: number; // 0..1
  lfoTarget: LfoTarget;
  attack: number; // s
  decay: number; // s
  sustain: number; // 0..1
  release: number; // s
}

export interface DrumParams {
  kit: 'classic';
}

export type ChannelKind = 'synth' | 'drum' | 'sampler';

export interface SamplerParams {
  sampleId: string | null; // id into the sample bank
  rootNote: number; // MIDI note the sample plays at at 1x rate
  loop: boolean;
}

export interface EffectSlot {
  id: string;
  type: string; // key into the effect registry
  enabled: boolean;
  params: Record<string, number>;
}

export interface Bus {
  id: string;
  name: string;
  volume: number; // 0..1.5
  pan: number; // -1..1
  effects: EffectSlot[];
}

export interface Channel {
  id: string;
  name: string;
  kind: ChannelKind;
  color: string;
  synth: SynthParams;
  drum: DrumParams;
  sampler: SamplerParams;
  patternId: string;
  muted: boolean;
  solo: boolean;
  volume: number; // 0..1.5 (1 = unity)
  pan: number; // -1..1
  effects: EffectSlot[]; // insert chain
  sends: Record<string, number>; // busId → 0..1
}

export interface Note {
  id: string;
  start: number; // step (integer)
  length: number; // steps, >= 1
  note: number; // MIDI 0..127
  velocity: number; // 0..127
}

export interface Pattern {
  id: string;
  name: string;
  length: number; // steps
  notes: Note[];
}

export interface PlaylistClip {
  id: string;
  channelId: string;
  patternId: string;
  startStep: number; // absolute step
  length: number; // steps
}

/**
 * A session-view scene: one row of the launch grid. `cells` maps
 * channelId → patternId (or null for an empty cell). Launching a scene
 * starts every assigned clip simultaneously; each clip loops.
 */
export interface Scene {
  id: string;
  name: string;
  cells: Record<string, string | null>;
}

export interface LoopRange {
  enabled: boolean;
  startStep: number;
  endStep: number; // exclusive
}

export interface Project {
  name: string;
  bpm: number;
  timeSig: { beats: number; unit: number };
  countInBars: number; // 0 = off, else bars of metronome before playback
  loop: LoopRange;
  patterns: Record<string, Pattern>;
  channels: Channel[];
  buses: Bus[];
  playlist: PlaylistClip[];
  scenes: Scene[];
  masterVolume: number; // 0..1.5
}

// ── Defaults ────────────────────────────────────────────────────────────────

export const CHANNEL_COLORS = [
  '#4cc2ff',
  '#b06bff',
  '#3ddc84',
  '#ffb454',
  '#ff6b9d',
  '#7dd3fc',
  '#f472b6',
  '#a3e635',
];

export function defaultSynthParams(): SynthParams {
  return {
    osc1Wave: 'sawtooth',
    osc1Octave: 0,
    osc1Detune: 0,
    osc1Level: 0.7,
    osc2Wave: 'sawtooth',
    osc2Octave: 0,
    osc2Detune: 7,
    osc2Level: 0.4,
    fmDepth: 0,
    filterType: 'lowpass',
    filterCutoff: 6000,
    filterResonance: 4,
    filterEnvAmount: 0.35,
    lfoRate: 0,
    lfoDepth: 0,
    lfoTarget: 'filter',
    attack: 0.005,
    decay: 0.18,
    sustain: 0.55,
    release: 0.25,
  };
}

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export function noteName(midi: number): string {
  return `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
}

export function uid(): string {
  return crypto.randomUUID();
}

/** Steps (16th notes) per bar for a time signature. */
export function stepsPerBarFor(beats: number, unit: number): number {
  return Math.max(4, Math.round((beats * 16) / unit));
}